import { useCallback, useEffect } from "react";
import { UseFormReturn } from "react-hook-form";
import CreateEditShipmentData from "./CreateEditShipmentData";
import CreateShipmentData from "./CreateEditShipmentData";

const isFiniteCoordValue = (value: unknown): boolean => {
  if (typeof value === "number") {
    return Number.isFinite(value);
  }
  if (typeof value === "string") {
    return value.trim() !== "" && Number.isFinite(Number(value));
  }
  return false;
};

// Manually-typed coordinates (the "switch to coords" input) can be mid-entry
// (e.g. only one of lat/lon filled in), which produces a coord object that
// exists but isn't a real point yet — exclude those, not just null/undefined.
const isValidCoord = (
  coord: { lat: unknown; lon: unknown } | null | undefined
): coord is { lat: number; lon: number } =>
  !!coord && isFiniteCoordValue(coord.lat) && isFiniteCoordValue(coord.lon);

const useCalculateEstimatedShipmentDistance = (
  formMethods: UseFormReturn<CreateEditShipmentData>,
  stopsCoords: { lat: number; lon: number }[],
  setStopsCoords: (coords: { lat: number; lon: number }[]) => void,
  setEstimatedDistance: (distance: number | null) => void
) => {
  const { getValues, watch } = formMethods;

  const reactOnStopsLocationsChange = useCallback(
    async (values: CreateShipmentData) => {
      const stopsCoordsInOrder = [...values.pickupList, ...values.dropoffList]
        .map((s) => s.place?.coord)
        .filter(isValidCoord);
      if (JSON.stringify(stopsCoords) !== JSON.stringify(stopsCoordsInOrder)) {
        // The distance itself is fetched by the effect on stopsCoords in
        // index.tsx; fetching here too made every stop change call HERE twice.
        setStopsCoords(stopsCoordsInOrder);
        if (stopsCoordsInOrder.length < 2) {
          setEstimatedDistance(null);
        }
      }
    },
    [stopsCoords, setEstimatedDistance, setStopsCoords]
  );

  const run = useCallback(
    (_: any) => {
      const values = getValues();
      reactOnStopsLocationsChange(values);
    },
    [getValues, reactOnStopsLocationsChange]
  );

  return useEffect(() => {
    const subscription = watch((_, changes: any) => run(changes));
    return () => subscription.unsubscribe();
  }, [watch, run]);
};

export default useCalculateEstimatedShipmentDistance;
